import React, { useEffect, useCallback, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import { BubbleMenu } from '@tiptap/react/menus';
import StarterKit from '@tiptap/starter-kit';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import ListItem from '@tiptap/extension-list-item';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Link from '@tiptap/extension-link';
import { TextStyle } from '@tiptap/extension-text-style';
import Highlight from '@tiptap/extension-highlight';
import { NodeSelection } from '@tiptap/pm/state';
import api from '../../../../services/api';
import SuccessToast from '../../../../shared/components/ui/SuccessToast';

// ── Collaboration (additive – safe to tree-shake when props not provided) ─────
import CollaborationBar, { useTypingIndicator } from './CollaborationBar';
import Collaboration from '@tiptap/extension-collaboration';
import CollaborationCaret from '@tiptap/extension-collaboration-caret';

// ── Extracted extensions ───────────────────────────────────────────────────────
import CustomImage from './extensions/ImageExtension';
import { CustomOrderedList, CustomBulletList, CustomTableCell, CustomTableHeader, RaciDecorationExtension, applyRaciColors } from './extensions/EditorExtensions';
import sanitizePastedHTML from './extensions/sanitizePastedHTML';
import ImageSettingsPanel from './components/ImageSettingsPanel';

// ─── Main editor component ────────────────────────────────────────────────────
// collab* props are all OPTIONAL – omitting them keeps the editor in normal mode
const UnifiedTiptapEditor = ({ htmlContent, onChange, editorRef, isFullScreen, rfpId,
    // Collaboration props (all optional – editor degrades gracefully without them)
    collabYdoc,        // Y.Doc instance from useCollaboration
    collabProvider,    // WebsocketProvider instance from useCollaboration
    collabUser,        // { name, color, initials } – local user metadata
    collabStatus,      // 'connected' | 'connecting' | 'disconnected' | 'unavailable'
    collaborators,     // remote user array from useCollaboration
    collabReady,       // sync status from useCollaboration
}) => {
    const wrapperRef = useRef(null);
    const editorElRef = useRef(null);
    const lastHtmlRef = useRef(htmlContent);
    const isLocalChangeRef = useRef(false);
    const hasSeededRef = useRef(false);
    // Track whether ANY remote Yjs update has been received for this doc instance.
    // If true it means the server has content (User 1's edits) so we must NOT overwrite.
    const hasReceivedRemoteUpdateRef = useRef(false);

    const [imageContextMenu, setImageContextMenu] = React.useState(null);
    const [toast, setToast] = React.useState(null);
    const uploadAndInsertImageRef = useRef();
    const onChangeRef = useRef(onChange);

    useEffect(() => {
        onChangeRef.current = onChange;
    }, [onChange]);

    // ── Collaboration extension list (only added when collabYdoc is live) ────
    const isCollabActive = !!(collabYdoc && collabProvider && collabStatus === 'connected');

    const collabExtensions = isCollabActive ? [
        Collaboration.configure({ document: collabYdoc }),
        CollaborationCaret.configure({
            provider: collabProvider,
            user: collabUser || { name: 'Collaborator', color: '#7C3AED' },
        }),
    ] : [];

    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                bulletList: false,
                orderedList: false,
                history: !isCollabActive,
            }),
            CustomBulletList,
            CustomOrderedList,
            ListItem,
            Table.configure({ resizable: true, lastColumnResizable: true, allowTableNodeSelection: true }),
            TableRow,
            CustomTableCell,
            CustomTableHeader,
            RaciDecorationExtension,
            Underline,
            CustomImage.configure({ HTMLAttributes: { class: 'editor-embedded-image' }, allowBase64: false }),
            TextAlign.configure({ types: ['heading', 'paragraph', 'tableCell', 'tableHeader'], alignments: ['left', 'center', 'right', 'justify'] }),
            Link.configure({ openOnClick: false, autolink: true, defaultProtocol: 'https', HTMLAttributes: { class: 'editor-link', target: '_blank', rel: 'noopener noreferrer' } }),
            TextStyle,
            Highlight.configure({ multicolor: true }),
            ...collabExtensions,
        ],
        content: isCollabActive ? undefined : (htmlContent || ''),
        editorProps: {
            attributes: { class: 'seamless-tiptap-content' },
            transformPastedHTML(html) { return sanitizePastedHTML(html); },
            handleDOMEvents: {
                click: (view, event) => {
                    const target = event.target;
                    if (target.nodeName === 'IMG') {
                        const pos = view.posAtDOM(target);
                        if (pos >= 0) { const { state, dispatch } = view; dispatch(state.tr.setSelection(NodeSelection.create(state.doc, pos))); }
                        event.stopPropagation();
                        return true;
                    }
                    return false;
                },
                contextmenu: (view, event) => {
                    const target = event.target;
                    if (target.nodeName === 'IMG') {
                        event.preventDefault();
                        const pos = view.posAtDOM(target);
                        if (pos >= 0) { const { state, dispatch } = view; dispatch(state.tr.setSelection(NodeSelection.create(state.doc, pos))); }
                        setImageContextMenu({ x: event.clientX, y: event.clientY, target });
                        return true;
                    }
                    return false;
                },
                drop: (view, event) => {
                    const hasFiles = event.dataTransfer?.files?.length > 0;
                    if (hasFiles) {
                        const file = event.dataTransfer.files[0];
                        if (file.type.startsWith('image/')) {
                            event.preventDefault();
                            const coordinates = view.posAtCoords({ left: event.clientX, top: event.clientY });
                            if (coordinates && uploadAndInsertImageRef.current) uploadAndInsertImageRef.current(file, coordinates.pos);
                            else if (uploadAndInsertImageRef.current) uploadAndInsertImageRef.current(file);
                            return true;
                        }
                    }
                    return false;
                },
                paste: (view, event) => {
                    const hasItems = event.clipboardData?.items?.length > 0;
                    if (hasItems) {
                        const item = event.clipboardData.items[0];
                        if (item.type.startsWith('image/')) {
                            const file = item.getAsFile();
                            if (file && uploadAndInsertImageRef.current) { event.preventDefault(); uploadAndInsertImageRef.current(file); return true; }
                        }
                    }
                    return false;
                }
            }
        },
        onUpdate: ({ editor }) => {
            const html = editor.getHTML();
            lastHtmlRef.current = html;
            isLocalChangeRef.current = true;
            clearTimeout(isLocalChangeRef._timer);
            isLocalChangeRef._timer = setTimeout(() => { isLocalChangeRef.current = false; }, 300);
            if (onChangeRef.current) onChangeRef.current(html);
            applyRaciColors(editorElRef.current);
        },
    });

    // ── Typing indicator – notifies collab peers via awareness ────────────────
    const { notifyTyping } = useTypingIndicator(collabProvider || null);

    const showToast = (message, type = 'success') => { setToast({ message, type }); setTimeout(() => setToast(null), 4000); };

    // Ref-safe S3 image upload and cursor/drop insert
    uploadAndInsertImageRef.current = async (file, pos) => {
        if (!rfpId || !editor) return;
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif', 'image/svg+xml'];
        if (!allowedTypes.includes(file.type)) { showToast('Format must be JPG, JPEG, PNG, WEBP, GIF, or SVG.', 'error'); return; }
        if (file.size > 5 * 1024 * 1024) { showToast('Image size must be less than 5MB.', 'error'); return; }
        showToast('Uploading image to secure storage...', 'info');
        const formData = new FormData();
        formData.append('file', file);
        try {
            const response = await api.post(`/rfp/${rfpId}/upload-image`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
            const linkUrl = response.data?.url || response.data?.link;
            if (linkUrl) {
                const imageAttrs = { src: linkUrl, alt: 'Embedded Image', 'data-width-percent': '50', 'data-align': 'center' };
                if (pos !== undefined && pos !== null) editor.chain().focus().insertContentAt(pos, { type: 'image', attrs: imageAttrs }).run();
                else editor.chain().focus().setImage(imageAttrs).run();
                showToast('Image inserted successfully.');
            } else { throw new Error('No URL returned'); }
        } catch (err) { console.error('S3 drop/paste upload failed:', err); showToast('Failed to upload image.', 'error'); }
    };

    const handleDeleteImageForNode = async (img) => {
        const src = img.getAttribute('src');
        if (src && rfpId) {
            try {
                const filename = src.split('/').pop();
                await api.delete(`/rfp/${rfpId}/image/${filename}`);
                showToast('Image deleted successfully');
            } catch (err) { console.error('Failed to delete image:', err); showToast('Failed to delete image', 'error'); }
        }
        img.remove();
        editor.commands.setContent(editorElRef.current.innerHTML, false);
        setImageContextMenu(null);
    };

    const handleDeleteImage = async () => {
        if (!imageContextMenu?.target) return;
        await handleDeleteImageForNode(imageContextMenu.target);
    };

    useEffect(() => {
        const handleClose = (e) => {
            if (e.target.closest('.image-settings-panel') || e.target.nodeName === 'IMG' || e.target.closest('.image-resize-handle') || e.target.closest('.simple-table-toolbar')) return;
            setImageContextMenu(null);
        };
        window.addEventListener('click', handleClose);
        return () => window.removeEventListener('click', handleClose);
    }, []);

    useEffect(() => {
        if (editor && editorRef) editorRef.current = editor;
        if (editor && editorElRef.current) applyRaciColors(editorElRef.current);
    }, [editor, editorRef]);

    const setEditorEl = useCallback((node) => {
        if (node) { editorElRef.current = node; setTimeout(() => applyRaciColors(node), 100); }
    }, []);

    useEffect(() => {
        if (!editor) return;
        const handler = () => applyRaciColors(editorElRef.current);
        editor.on('selectionUpdate', handler);
        editor.on('transaction', handler);
        return () => { editor.off('selectionUpdate', handler); editor.off('transaction', handler); };
    }, [editor]);

    // ── Sync external content changes (e.g. AI Improve or version revert) ───────
    useEffect(() => {
        if (!editor) return;
        if (htmlContent === lastHtmlRef.current) return;
        if (isLocalChangeRef.current) { lastHtmlRef.current = htmlContent; return; }
        if (isCollabActive) return;
        if (htmlContent !== editor.getHTML()) {
            editor.commands.setContent(htmlContent || '', false);
            lastHtmlRef.current = htmlContent;
            setTimeout(() => applyRaciColors(editorElRef.current), 80);
        }
    }, [htmlContent, editor, isCollabActive]);

    // ── Seed & Wipe-guard for Yjs collaborative document ─────────────────────
    useEffect(() => {
        if (!editor || !isCollabActive || !collabReady || !collabYdoc) return;

        // Reset remote-update tracker whenever we get a fresh ydoc instance.
        hasReceivedRemoteUpdateRef.current = false;

        // Step 1: Subscribe to ydoc-level updates BEFORE reading fragment length.
        // This tells us if the server is about to send User 1's state.
        // We must register this BEFORE we check isEmpty, to avoid a race condition.
        const handleYdocUpdate = (update, origin) => {
            // In y-websocket, remote (server-sent) updates arrive with origin === null.
            // Local user edits arrive with origin === the WebsocketProvider instance.
            // We only care about remote updates to know if User 1's state arrived.
            if (origin === null) {
                hasReceivedRemoteUpdateRef.current = true;
            }
        };
        collabYdoc.on('update', handleYdocUpdate);

        const fragment = collabYdoc.getXmlFragment('prosemirror');

        // Step 2: Defer the empty-room seeding check by 400ms.
        // WHY: The y-websocket `sync` event fires as soon as the WebSocket handshake
        // completes, but the actual document state (User 1's edits) arrives in a
        // SEPARATE message packet immediately after. If we check fragment.length
        // synchronously, the fragment is still empty for User 2 even when User 1
        // has made changes. Deferring 400ms ensures the update packet has arrived.
        const seedTimer = setTimeout(() => {
            if (hasSeededRef.current) return;
            hasSeededRef.current = true;

            const isYjsEmpty = fragment.length === 0;
            // Only seed from DB if no remote content arrived AND fragment is still empty.
            if (isYjsEmpty && !hasReceivedRemoteUpdateRef.current && htmlContent) {
                console.log('[Collab Seed] Room confirmed empty – seeding from database content.');
                editor.commands.setContent(htmlContent, false);
                lastHtmlRef.current = htmlContent;
            } else if (!isYjsEmpty) {
                // Room already has content (User 1's edits). Sync our lastHtmlRef
                // to what Yjs has so future saves reflect the collaborative state.
                const currentHtml = editor.getHTML();
                lastHtmlRef.current = currentHtml;
                console.log('[Collab Seed] Room has existing content – skipping DB seed. Preserving collaborative state.');
            }
        }, 400);

        // Step 3: Wipe-guard – detect if server unexpectedly empties the room
        // (e.g. backend clears room when User 1 disconnects) and restore content.
        let wipeGuardTimer = null;
        const handleFragmentChange = () => {
            clearTimeout(wipeGuardTimer);
            wipeGuardTimer = setTimeout(() => {
                if (fragment.length === 0 && lastHtmlRef.current &&
                    lastHtmlRef.current !== '<p></p>' && lastHtmlRef.current !== '') {
                    console.warn('[Collab Failsafe] Yjs room wiped unexpectedly. Restoring current editor content...');
                    editor.commands.setContent(lastHtmlRef.current, false);
                }
            }, 150); // short debounce so normal Yjs ops don't false-fire
        };
        fragment.observe(handleFragmentChange);

        return () => {
            clearTimeout(seedTimer);
            clearTimeout(wipeGuardTimer);
            collabYdoc.off('update', handleYdocUpdate);
            fragment.unobserve(handleFragmentChange);
        };
    }, [editor, isCollabActive, collabReady, collabYdoc, htmlContent]);

    if (!editor) return null;

    return (
        <div
            className={`unified-seamless-editor-wrapper${isFullScreen ? ' fullscreen-editor' : ''}`}
            ref={wrapperRef}
            onKeyDown={isCollabActive ? notifyTyping : undefined}
        >
            {/* ── Collaboration bar (additive – only when collab props are provided) */}
            {(collabYdoc || collabStatus) && (
                <CollaborationBar
                    collaborators={collaborators || []}
                    status={collabStatus || 'unavailable'}
                    provider={collabProvider || null}
                />
            )}
            {editor && (
                <BubbleMenu editor={editor} shouldShow={({ editor }) => editor.isActive('table')} tippyOptions={{ duration: 100, maxWidth: 'none', placement: 'top-start' }}>
                    <div className="simple-table-toolbar">
                        <button onClick={() => editor.chain().focus().addRowAfter().run()}>+ Row</button>
                        <button onClick={() => editor.chain().focus().addColumnAfter().run()}>+ Col</button>
                        <div className="v-divider" />
                        <button onClick={() => editor.chain().focus().mergeCells().run()}>Merge</button>
                        <button onClick={() => editor.chain().focus().splitCell().run()}>Split</button>
                        <div className="v-divider" />
                        <button onClick={() => editor.chain().focus().deleteRow().run()}>Del Row</button>
                        <button onClick={() => editor.chain().focus().deleteColumn().run()}>Del Col</button>
                        <div className="v-divider" />
                        <button onClick={() => editor.chain().focus().toggleHeaderRow().run()}>Header Row</button>
                        <div className="v-divider" />
                        <button onClick={() => editor.chain().focus().deleteTable().run()} className="table-delete-text">Delete Table</button>
                    </div>
                </BubbleMenu>
            )}
            {editor && (
                <BubbleMenu editor={editor} shouldShow={({ editor }) => editor.isActive('link')} tippyOptions={{ duration: 100, placement: 'bottom-start' }}>
                    <div className="simple-table-toolbar">
                        <button onClick={() => { const url = editor.getAttributes('link').href; if (url) window.open(url, '_blank'); }}>Open</button>
                        <div className="v-divider" />
                        <button onClick={() => editor.chain().focus().unsetLink().run()} className="table-delete-text">Remove Link</button>
                    </div>
                </BubbleMenu>
            )}
            <div className="unified-seamless-editor" ref={setEditorEl}>
                <EditorContent editor={editor} />
            </div>
            {imageContextMenu && (
                <ImageSettingsPanel
                    target={imageContextMenu.target}
                    editor={editor}
                    wrapperRef={wrapperRef}
                    rfpId={rfpId}
                    onClose={() => setImageContextMenu(null)}
                    onDelete={handleDeleteImage}
                />
            )}
            <SuccessToast toast={toast} />
        </div>
    );
};

export default UnifiedTiptapEditor;
